'use client'

import { Turnstile, type TurnstileProps } from '@marsidev/react-turnstile'
import { useState, useSyncExternalStore } from 'react'

export type TurnstileChallengeProps = {
  action: 'forgot_password' | 'login' | 'register'
  onTokenChange: (token: string | null) => void
  resetKey?: number
  siteKey: string
  testMode?: boolean
}

const subscribeToBrowserState = () => () => undefined

export function TurnstileChallenge({
  action,
  onTokenChange,
  resetKey = 0,
  siteKey,
  testMode = false,
}: TurnstileChallengeProps) {
  const [errorCode, setErrorCode] = useState<string | null>(null)
  const isBrowser = useSyncExternalStore(
    subscribeToBrowserState,
    () => true,
    () => false,
  )

  if (testMode) {
    return (
      <button
        key={resetKey}
        type="button"
        data-testid={`turnstile-${action}-test-challenge`}
        onClick={() => onTokenChange(`e2e-${action}-turnstile-token`)}
        className="rounded-md border border-border px-4 py-2 text-sm text-foreground"
      >
        Complete security verification
      </button>
    )
  }

  if (!siteKey) {
    return (
      <p role="alert" className="text-sm text-brand-red">
        Security verification is unavailable. Please try again later.
      </p>
    )
  }

  return (
    <div className="space-y-2">
      {isBrowser ? (
        <Turnstile
          key={resetKey}
          siteKey={siteKey}
          onSuccess={(token) => {
            setErrorCode(null)
            onTokenChange(token)
          }}
          onError={(code?: string) => {
            setErrorCode(String(code || 'unknown'))
            onTokenChange(null)
          }}
          onExpire={() => onTokenChange(null)}
          options={
            {
              action,
              refreshExpired: 'auto',
              retry: 'auto',
              size: 'normal',
              theme: 'auto',
            } as TurnstileProps['options']
          }
        />
      ) : (
        <div
          aria-hidden="true"
          className="h-[65px] w-full min-w-[300px]"
          data-turnstile-placeholder
        />
      )}
      {errorCode ? (
        <p role="alert" className="text-sm text-brand-red">
          Security verification could not complete (code {errorCode}). Refresh the page or try
          another browser.
        </p>
      ) : null}
    </div>
  )
}
